/*
  A javascript version of com.videx.misc.TimeOfDay.
  Do not use this constructor but instead use TimeOfDayFactory to
  create valid TimeOfDay instances.
*/
export function TimeOfDay(seconds) {
    //DO NOT MODIFY THESE FIELDS
    this.SECONDS_PER_HOUR = 60 * 60;
    this.SECONDS_PER_DAY = 24 * 60 * 60;
    this.seconds = seconds;

    /**
     * Get the hour within the day (on the 24 hour clock).
     * @return 0 through 23
     * */
    this.getHourOfDay24 = function() {
        return Math.floor(this.seconds / this.SECONDS_PER_HOUR);
    }

    /**
     * Get the hour within the day (on the 12 hour clock).
     * Call isMoring() to determine which half of the day the hour
     * refers to.
     * @return 1 through 12
     * */
    this.getHourOfDay12 = function() {
        const hour = this.getHourOfDay24();
        if (hour === 0)
            return 12;
        else if (hour > 12)
            return hour - 12;
        else
            return hour;
    }

    /**
     * Get the minute within the hour.
     * @return 0 through 59
     * */
    this.getMinuteOfHour = function() {
        const minAndSec = seconds - (this.getHours() * this.SECONDS_PER_HOUR);
        return Math.floor(minAndSec / 60);
    }

    /**
     * Get the second within the minute.
     * @return 0 through 59
     * */
    this.getSecondOfMinute = function() {
        return this.seconds % 60;
    }

    /**
     * Return true if the time of day is in the morning.  Morning is defined to last
     * from midnight until the instant it becomes noon (12:00PM).
     * */
    this.isMorning = function() {
        return this.getHours() < 12;
    }

    /**
     * Get the integral number of hours elapsed since midnight.
     * Same as getHourOfDay24
     * @see TimeOfDay#getHourOfDay24()
     * */
    this.getHours = function() {
        return this.getHourOfDay24();
    }

    /**
     * Get the integral number of minutes elapsed since midnight.
     * */
    this.getMinutes = function() {
        return Math.floor(this.seconds / 60);
    }

    /**
     * Get the total number of seconds elapsed since midnight.
     * */
    this.getSeconds = function() {
        return this.seconds;
    }
}

/*
  A factor for creating valid TimeOfDay objects.
*/
export class TimeOfDayFactory {
    constructor() {
        //used for accessing constants
        this._tod = new TimeOfDay(0);
    }

    /**
     * Create a TimeOfDay given a number of seconds elapsed since midnight.
     * @return null if the given value is not within the valid range
     * */
    createFromSeconds(secondsSinceMidnight) {
        if (secondsSinceMidnight > -1 && secondsSinceMidnight < (this._tod.SECONDS_PER_DAY))
            return new TimeOfDay(secondsSinceMidnight);
        else
            return null;
    }

    /**
     * Create a TimeOfDay given the hour, minute, and second (on a 24 hour clock).
     * @return null if any of the given values are not within the valid range
     */
    create24(hours, minutes, seconds) {
        if (
            hours > -1 && hours < 24
            && minutes > -1 && minutes < 60
            && seconds > -1 && seconds < 60
        ) {
            return new TimeOfDay(hours * this._tod.SECONDS_PER_HOUR + minutes * 60 + seconds);
        }

        return null;
    }

    /**
     * Create a TimeOfDay given the hour, minute, second, and am/pm (on the 12 hour clock).
     * @return null if any of the given values are not within the valid range
     */
    create12(hours, minutes, seconds, isAM) {
        if (
            hours > 0 && hours < 13
            && minutes > -1 && minutes < 60
            && seconds > -1 && seconds < 60
        ) {
            if (hours === 12)
                hours = 0;
            if (!isAM)
                hours += 12;

            return new TimeOfDay(hours * this._tod.SECONDS_PER_HOUR + minutes * 60 + seconds);
        }

        return null;
    }
}
